import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Dropdown, Input, Menu, Message, Select } from "@arco-design/web-react";
import { IconCheckCircle, IconMessage as IdeaIcon } from "@arco-design/web-react/icon";
import styled from "styled-components";

import ImproveButton from "../../Components/ImproveButton";
import EmailEditor from "../../Broadcast/BroadcastEditor/MailVariants/EmailEditor/EmailEditor";
import { EDITOR_EASY_EMAIL, EDITOR_CODE } from "../../Broadcast/BroadcastEditor/MailVariants/EmailEditor/Html/HtmlEditor";
import EmailViewer from "../../Broadcast/BroadcastEditor/MailVariants/EmailPreviewer/EmailViewer";
import { SuggestionsDialog } from "../../Broadcast/BroadcastEditor/MailVariants/SuggestionsDialog";
import { PersonalizationDialog } from "../../Broadcast/BroadcastEditor/MailVariants/PersonalizationDialog";
import { CONFIRM_URL_TAG, confirmButtonHtml } from "../EasyEmail/campaignTags";

const copyToClipboard = (text, done) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => Message.success(done), () => Message.info("Copy this: " + text));
    } else {
        Message.info("Copy this: " + text);
    }
};

// EmailComposer is the single reusable email-editing surface shared by the
// broadcast, campaign, and sequence editors. It OWNS the editable content
// (subject / html / text / Easy Email design + which editor mode is active),
// handles template loading and the AI helpers, and exposes getContent() via a
// ref so the parent can pull the final payload at save time. The parent owns the
// data fetch, routing/breadcrumbs, the Edit/Save/Delete toolbar, and any
// feature-specific fields.
//
// Props:
//   initial            {subject, html, text, design} seed values
//   seedKey            re-seed from `initial` whenever this changes (record load / cancel)
//   disabled           true => read-only preview (EmailViewer); false => editable
//   templates          array of email templates; when present + editable a picker is shown
//   enablePersonalize  show the merge-tag "Personalize Email" helper (broadcast/sequence)
//   confirmLink        double opt-in signup email: offer the confirm link (a
//                      "Confirm button" block in the visual editor, a copy menu
//                      for the code editor and the plain-text version)
//   onFlowLink         called when a confirm/unsubscribe/tracked link is clicked
//                      in the preview (the campaign editor offers Test drive)
//   editorHeight       Easy Email height (default fills the viewport)
//   requireSubject     the subject is required (marked so; see flagSubject)
// Ref:
//   getContent() -> { mail_subject_template, mail_html_template, mail_text_template, editor_design }
//   flagSubject()      shows the (required) subject as missing and focuses it

const EmailViewerBody = styled.div`
    min-height: 450px;
    max-height: 550px;
    overflow-y: scroll;
    border: 1px solid #c2c2c2;
    padding: 10px;
    height: 100%;
    width: 100%;
`;
const EmailViewerToolBar = styled.div`
    max-height: 550px;
    background: #e1e1e1;
    border-top: 1px solid #c2c2c2;
    border-left: 1px solid #c2c2c2;
    border-right: 1px solid #c2c2c2;
`;

function editorModeFor(design, html) {
    const hasDesign = design && design.content;
    return hasDesign ? EDITOR_EASY_EMAIL : (html ? EDITOR_CODE : EDITOR_EASY_EMAIL);
}

const EmailComposer = forwardRef(function EmailComposer(
    { initial, seedKey, disabled = false, templates, enablePersonalize = false, confirmLink = false, onFlowLink = null, editorHeight = "calc(100vh - 240px)", requireSubject = false },
    ref
) {
    // State is LAZY-initialized from `initial` so the (heavy) Easy Email editor
    // mounts exactly once with the right design — parents that already have the
    // record at mount (the common case) avoid a wasteful blank-then-real remount.
    const init0 = initial || {};
    const [subject, setSubject] = useState(() => "" + (init0.subject || ""));
    const [htmlContent, setHtmlContent] = useState(() => "" + (init0.html || ""));
    const [textContent, setTextContent] = useState(() => "" + (init0.text || ""));
    const [editorDesign, setEditorDesign] = useState(() => (init0.design && init0.design.content ? init0.design : null));
    const [editor, setEditor] = useState(() => editorModeFor(init0.design, init0.html));
    // Bumped on every RE-seed so the Easy Email editor remounts and shows the
    // freshly-loaded design (it seeds its form once at mount).
    const [designKey, setDesignKey] = useState(0);
    const easyEmailRef = useRef(null);
    const subjectRef = useRef(null);
    const [subjectMissing, setSubjectMissing] = useState(false);

    const seed = (values) => {
        const html = "" + (values.html || "");
        const design = values.design && values.design.content ? values.design : null;
        setSubject("" + (values.subject || ""));
        setHtmlContent(html);
        setTextContent("" + (values.text || ""));
        setEditorDesign(design);
        setEditor(editorModeFor(design, html));
        setDesignKey((k) => k + 1);
    };

    // Re-seed only when the parent asks (a CHANGED seedKey after mount): a record
    // reload or a cancel/reset. The first run is skipped because the state above is
    // already seeded from `initial` — re-seeding on mount would remount Easy Email
    // for nothing. (`initial` is intentionally out of deps so an unstable object
    // literal can't reseed every render and wipe in-progress edits.)
    const seededOnce = useRef(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!seededOnce.current) { seededOnce.current = true; return; }
        seed(initial || {});
    }, [seedKey]);

    useImperativeHandle(ref, () => ({
        flagSubject: () => {
            setSubjectMissing(true);
            if (subjectRef.current) subjectRef.current.focus();
        },
        getContent: () => {
            let html = htmlContent;
            let design = editorDesign;
            if (editor === EDITOR_EASY_EMAIL && easyEmailRef.current) {
                try {
                    const out = easyEmailRef.current.getContent();
                    html = out.html;
                    design = out.design;
                } catch (e) {
                    // Compiling the Easy Email design to HTML (MJML) can throw —
                    // e.g. an empty/blank page. Never let that abort the save; keep
                    // the last-known html/design so the record still persists.
                    console.error("Easy Email getContent failed; saving last-known content", e);
                }
            }
            return {
                mail_subject_template: subject,
                mail_html_template: html,
                mail_text_template: textContent,
                editor_design: design,
            };
        },
    }), [subject, htmlContent, textContent, editorDesign, editor]);

    const loadFromTemplate = (templateId) => {
        const t = (templates || []).find((x) => x.id === templateId);
        if (!t) return;
        seed({
            subject: t.mail_subject_template,
            html: t.mail_html_template,
            text: t.mail_text_template,
            design: t.editor_design,
        });
        Message.success("Loaded template: " + t.name);
    };

    if (disabled) {
        return (
            <div>
                <div style={{ padding: "4px 0", marginBottom: 8 }}>
                    <span style={{ color: "var(--color-text-3)", fontWeight: 500, marginRight: 8 }}>Subject:</span>
                    <span style={{ color: subject ? "var(--color-text-1)" : "var(--color-text-3)" }}>{subject || "No subject"}</span>
                </div>
                <EmailViewer
                    classNameBody={EmailViewerBody.styledComponentId}
                    classNameToolBar={EmailViewerToolBar.styledComponentId}
                    htmlContent={htmlContent}
                    textContent={textContent}
                    subject={subject}
                />
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <SuggestionsDialog
                    data={{ a: "foo" }}
                    render={(open) => (
                        <Button icon={<IdeaIcon />} onClick={open}>Ideas</Button>
                    )}
                    dataSetter={setHtmlContent}
                    marketingType={"body_html"}
                />
                {enablePersonalize && (
                    <PersonalizationDialog
                        render={(open) => (
                            <Button icon={<IdeaIcon />} onClick={open}>Personalize Email</Button>
                        )}
                    />
                )}
                {confirmLink && (
                    <Dropdown
                        trigger="click"
                        droplist={
                            <Menu onClickMenuItem={(key) => copyToClipboard(key === "button" ? confirmButtonHtml() : CONFIRM_URL_TAG,
                                key === "button" ? "Copied the confirm button: paste it into the code editor" : "Copied the confirm link: paste it into the plain-text version")}>
                                <Menu.Item key="button">Copy confirm button (HTML, for the code editor)</Menu.Item>
                                <Menu.Item key="url">Copy confirm link (for the plain-text version)</Menu.Item>
                            </Menu>
                        }
                    >
                        <Button icon={<IconCheckCircle />}>Confirm link</Button>
                    </Dropdown>
                )}
            </div>

            {templates && templates.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <Select
                        placeholder="Start from a template (optional)"
                        allowClear
                        style={{ width: "100%" }}
                        onChange={(v) => v && loadFromTemplate(v)}
                        options={templates.map((t) => ({ label: t.name, value: t.id }))}
                    />
                </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                {requireSubject && <span style={{ color: "rgb(var(--red-6))" }} aria-hidden>*</span>}
                <Input
                    ref={subjectRef}
                    style={{ flex: 1 }}
                    value={subject}
                    placeholder={requireSubject ? "Subject (required)" : "Subject"}
                    status={requireSubject && subjectMissing && !subject.trim() ? "error" : undefined}
                    aria-required={requireSubject || undefined}
                    onChange={(v) => { setSubject(v); setSubjectMissing(false); }}
                />
                <ImproveButton text={subject} onResult={(t) => { setSubject(t); setSubjectMissing(false); }} />
            </div>
            {requireSubject && subjectMissing && !subject.trim() && (
                <div style={{ color: "rgb(var(--red-6))", fontSize: 12, margin: "-8px 0 12px" }}>
                    A subject line is required: without one, inboxes show “(no subject)”.
                </div>
            )}

            <EmailEditor
                key={designKey}
                editor={editor}
                setEditor={setEditor}
                htmlContent={htmlContent}
                setHtmlContent={setHtmlContent}
                textContent={textContent}
                setTextContent={setTextContent}
                subject={subject}
                setSubject={setSubject}
                editorDesign={editorDesign}
                easyEmailRef={easyEmailRef}
                editorHeight={editorHeight}
                confirmLink={confirmLink}
                onFlowLink={onFlowLink}
            />
        </div>
    );
});

export default EmailComposer;
